import {
  Box,
  Button,
  Container,
  InputAdornment,
  TextField,
  Typography,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Add,
  Assignment,
  SearchOutlined,
  GridView,
  ViewList,
  Settings,
  LinkOutlined,
  People,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useGetListOfProjectsQuery,
  useGetProjectDashboardDataQuery,
  useGetConfigDetailsQuery,
} from "@/features/bughurd/api";
import JiraConnectDrawer from "./components/JiraConnectDrawer";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  resetBoardState,
  setSelectedItem,
  setStatusList,
} from "@/features/bughurd/slice";
import { BaseScrollableContainer } from "@/Components/scrollable/BaseScrollableContainer";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";

import ProjectListView from "./components/ProjectListView";
import ProjectCardView from "./components/ProjectCardView";

type ProjectPageProps = {
  reactFlow?: any;
  parentData?: any;
};

export default function ProjectPage({
  reactFlow,
  parentData,
}: ProjectPageProps) {
  const {
    roleCodeDetails: myPermissions,
    dateFormat,
    recordsPerPage,
  } = useAppSelector((state) => state.appGlobal);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const timeoutRef = useRef(null);
  const { t } = useTranslation(["bugherd"]);

  const baseUrl = window?.location?.origin;
  const isSniffer =
    baseUrl?.includes("sniffer") || baseUrl?.includes("pmohub") || false;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "cards">("list");
  const [boardTypeFilter, _setBoardTypeFilter] = useState<string>("all");
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 10,
    page: 1,
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, pageSize: recordsPerPage }));
  }, [recordsPerPage]);

  const {
    data: projectsData,
    isLoading,
    isFetching,
    refetch,
  } = useGetListOfProjectsQuery({
    searchName: tableSearch,
    page: paginationModel.page,
    size: paginationModel.pageSize,
    filterBy: boardTypeFilter !== "all" ? "boardType" : undefined,
    filterValue:
      boardTypeFilter !== "all" ? boardTypeFilter.toUpperCase() : undefined,
  });
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    isFetching: dashboardFetching,
  } = useGetProjectDashboardDataQuery({});
  const projects = projectsData?.data?.decryptedData;

  const boardDashboardData = dashboardData?.data?.decryptedData;

  const { data: configData } = useGetConfigDetailsQuery({});
  const whitelistedUrlsCount =
    configData?.data?.decryptedData?.find(
      (config: any) => config.type === "SNIFFER",
    )?.data?.whitelistedUrls?.length || 0;

  const requestSearch = useCallback(
    (val) => {
      setSearchValue(val);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setTableSearch(val);
      }, 400);
    },
    [setTableSearch],
  );

  useEffect(() => {
    dispatch(setSelectedItem(""));
    dispatch(resetBoardState());
    dispatch(setStatusList([]));
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    refetch();
  }, [boardTypeFilter]);

  return (
    <Box sx={{ mt: 3 }}>
      {/* <TaskLayout view="projectPage"> */}
      <BaseScrollableContainer>
        <Container
          maxWidth={false}
          sx={{
            width: "75%",
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              mb: 2,
              gap: { xs: 2, md: 0 },
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {t("My Projects")}
              </Typography>
              <Typography fontWeight={400}>
                {t("Manage and track all your projects in one place.")}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
              }}
            >
              {!DEFEKT_TRACKER.HIDE_VIEW(myPermissions) && !isSniffer && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigate("/defect-tracker/my-tasks");
                  }}
                  startIcon={<Assignment />}
                  disabled={!DEFEKT_TRACKER.VIEW(myPermissions)}
                >
                  {t("My Tasks")}
                </Button>
              )}
              {!DEFEKT_TRACKER.HIDE_CREATE(myPermissions) && (
                <Button
                  variant="outlined"
                  startIcon={<Settings />}
                  onClick={() => {
                    navigate("/defect-tracker/settings");
                  }}
                  disabled={!DEFEKT_TRACKER.CREATE(myPermissions)}
                  sx={{
                    borderColor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.main,
                    "&:hover": {
                      borderColor: (theme) => theme.palette.primary.dark,
                      backgroundColor: (theme) =>
                        `${theme.palette.primary.main}14`,
                    },
                  }}
                >
                  {t("Whitelisted URLs")}
                </Button>
              )}
              {!DEFEKT_TRACKER.HIDE_CREATE(myPermissions) && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setIsModalOpen(true)}
                  disabled={!DEFEKT_TRACKER.CREATE(myPermissions)}
                >
                  {t("Add New Project")}
                </Button>
              )}
            </Box>
          </Box>

          {/* Statistics Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(3, 1fr)",
              },
              gap: "16px",
              mb: "32px",
            }}
          >
            {[
              {
                icon: <Assignment sx={{ fontSize: 20 }} />,
                value: boardDashboardData?.totalProjects || 0,
                label: t("Total Projects"),
                colorKey: "primary" as const,
              },
              {
                icon: <People sx={{ fontSize: 22 }} />,
                value: boardDashboardData?.totalProjectMembers || 0,
                label: t("Total Project Members"),
                colorKey: "info" as const,
              },
              {
                icon: <LinkOutlined sx={{ fontSize: 22 }} />,
                value: whitelistedUrlsCount,
                label: t("Total Whitelisted URLs"),
                colorKey: "success" as const,
              },
            ].map((card, index) => (
              <Box
                key={index}
                sx={{
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                  backdropFilter: "blur(20px)",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)"
                    }`,
                  borderRadius: "16px",
                  p: 2,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                        : "0 8px 24px rgba(0, 0, 0, 0.08)",
                  },
                }}
              >
                {dashboardLoading || dashboardFetching ? (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Skeleton
                      variant="rounded"
                      width={44}
                      height={44}
                      sx={{ borderRadius: "12px", flexShrink: 0 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="50%" height={32} />
                      <Skeleton variant="text" width="70%" height={18} />
                    </Box>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        flexShrink: 0,
                        background: (theme) =>
                          `${theme.palette[card.colorKey].main}26`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: (theme) => theme.palette[card.colorKey].main,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Box>
                      <Typography
                        fontWeight={700}
                        sx={{ fontSize: "24px", lineHeight: 1.2 }}
                      >
                        {card.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: "13px",
                        }}
                      >
                        {card.label}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* View Toggle Buttons with Search */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              mb: 3,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                alignItems: { xs: "stretch", sm: "center" },
                flex: 1,
              }}
            >
              <TextField
                value={searchValue}
                onChange={(e) => requestSearch(e.target.value)}
                placeholder={t("Search projects...")}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: "250px" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : theme.palette.background.paper,
                  },
                }}
              />

              {/* <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: { xs: "center", sm: "flex-start" },
                  }}
                >
                  <Button
                    variant={boardTypeFilter === "all" ? "contained" : "outlined"}
                    onClick={() => setBoardTypeFilter("all")}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minWidth: { xs: "60px", sm: "70px" },
                      fontSize: { xs: "12px", sm: "14px" },
                      ...(boardTypeFilter === "all" && {
                        backgroundColor: (theme) => theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                      }),
                    }}
                  >
                    {t("All")}
                  </Button>
                  <Button
                    variant={
                      boardTypeFilter === "KANBAN" ? "contained" : "outlined"
                    }
                    onClick={() => setBoardTypeFilter("KANBAN")}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minWidth: { xs: "60px", sm: "80px" },
                      fontSize: { xs: "12px", sm: "14px" },
                      ...(boardTypeFilter === "KANBAN" && {
                        backgroundColor: (theme) => theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                      }),
                    }}
                  >
                    {t("Kanban")}
                  </Button>
                  <Button
                    variant={
                      boardTypeFilter === "SCRUM" ? "contained" : "outlined"
                    }
                    onClick={() => setBoardTypeFilter("SCRUM")}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minWidth: { xs: "60px", sm: "80px" },
                      fontSize: { xs: "12px", sm: "14px" },
                      ...(boardTypeFilter === "SCRUM" && {
                        backgroundColor: (theme) => theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                      }),
                    }}
                  >
                    {t("Scrum")}
                  </Button>
                </Box> */}
            </Box>

            {/* Items Per Page and Toggle Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                alignSelf: { xs: "center", md: "center" },
              }}
            >
              {/* Items Per Page Selector */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: { xs: "12px", sm: "13px" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("Show")}:
                </Typography>
                <FormControl size="small">
                  <Select
                    value={paginationModel.pageSize}
                    onChange={(e) => {
                      setPaginationModel((prev) => ({
                        ...prev,
                        pageSize: Number(e.target.value),
                        page: 1, // Reset to first page when changing page size
                      }));
                    }}
                    sx={{
                      minWidth: "70px",
                      fontSize: { xs: "12px", sm: "13px" },
                      height: "36px",
                      borderRadius: 2,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.2)"
                            : "rgba(0, 0, 0, 0.23)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: (theme) => theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Toggle Buttons */}
              <Box
                sx={{
                  display: "inline-flex",
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)"
                    }`,
                  borderRadius: 2,
                  padding: "4px",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                }}
              >
                <Button
                  onClick={() => setViewMode("cards")}
                  startIcon={<GridView sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    minWidth: { xs: "70px", sm: "85px" },
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.75 },
                    fontSize: { xs: "12px", sm: "13px" },
                    color: (theme) =>
                      viewMode === "cards"
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                    backgroundColor: (theme) =>
                      viewMode === "cards"
                        ? theme.palette.primary.main
                        : "transparent",
                    border: "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        viewMode === "cards"
                          ? theme.palette.primary.dark
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  {t("Cards")}
                </Button>
                <Button
                  onClick={() => setViewMode("list")}
                  startIcon={<ViewList sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    minWidth: { xs: "70px", sm: "85px" },
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.75 },
                    fontSize: { xs: "12px", sm: "13px" },
                    color: (theme) =>
                      viewMode === "list"
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                    backgroundColor: (theme) =>
                      viewMode === "list"
                        ? theme.palette.primary.main
                        : "transparent",
                    border: "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        viewMode === "list"
                          ? theme.palette.primary.dark
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  {t("List")}
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Conditional View Rendering */}
          {viewMode === "list" ? (
            <ProjectListView
              projects={projects}
              isLoading={isLoading}
              isFetching={isFetching}
              boardTypeFilter={boardTypeFilter}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
            />
          ) : (
            <ProjectCardView
              projects={projects}
              isLoading={isLoading}
              isFetching={isFetching}
              boardTypeFilter={boardTypeFilter}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
            />
          )}
        </Container>
      </BaseScrollableContainer>
      <JiraConnectDrawer open={isModalOpen} onClose={handleModalClose} />
      {/* </TaskLayout> */}
    </Box>
  );
}
